'use client'

import { useEffect, useRef, useState, useSyncExternalStore, type CSSProperties, type ReactNode } from 'react'

export type Role = 'blue' | 'green' | 'amber' | 'pink' | 'violet' | 'accent' | 'ink'

export function roleVar(role: Role): string {
  return role === 'ink' ? 'var(--gd-ink)' : `var(--gd-${role})`
}

export function fmt(n: number, digits = 2): string {
  if (!Number.isFinite(n)) return n > 0 ? '∞' : n < 0 ? '−∞' : '—'
  const s = n.toFixed(digits)
  const trimmed = s.includes('.') ? s.replace(/0+$/, '').replace(/\.$/, '') : s
  return (trimmed === '-0' ? '0' : trimmed).replace('-', '−')
}

/** Labelled range slider, coloured by the role of the quantity it controls. */
export function Slider({
  label,
  value,
  onChange,
  min,
  max,
  step = 0.01,
  role = 'ink',
  digits = 2,
  suffix,
  width,
}: {
  label: ReactNode
  value: number
  onChange: (v: number) => void
  min: number
  max: number
  step?: number
  role?: Role
  digits?: number
  suffix?: ReactNode
  width?: number | string
}) {
  const fill = ((value - min) / (max - min)) * 100
  return (
    <label className="flex min-w-0 items-center gap-3" style={{ width }}>
      <span className="shrink-0 text-[14px] font-semibold" style={{ color: roleVar(role), minWidth: 18 }}>
        {label}
      </span>
      <input
        type="range"
        className="gd-slider min-w-0 flex-1"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={e => onChange(Number(e.target.value))}
        style={{ '--gd-slider-color': roleVar(role), '--gd-fill': `${fill}%` } as CSSProperties}
      />
      <span className="gd-num w-[54px] shrink-0 text-right text-[13.5px]" style={{ color: 'var(--gd-ink)' }}>
        {fmt(value, digits)}
        {suffix}
      </span>
    </label>
  )
}

export function Segmented<T extends string | number>({
  options,
  value,
  onChange,
  label,
}: {
  options: { value: T; label: ReactNode }[]
  value: T
  onChange: (v: T) => void
  label?: string
}) {
  return (
    <div className="gd-seg" role="group" aria-label={label}>
      {options.map(o => (
        <button key={String(o.value)} type="button" aria-pressed={o.value === value} onClick={() => onChange(o.value)}>
          {o.label}
        </button>
      ))}
    </div>
  )
}

/** Readout pill: "label = value" in a role colour. */
export function Readout({ label, value, role = 'ink', big = false }: { label: ReactNode; value: ReactNode; role?: Role; big?: boolean }) {
  return (
    <div className="flex min-w-0 flex-col">
      <span className="gd-label !text-[10px]">{label}</span>
      <span className={`gd-num ${big ? 'text-[26px]' : 'text-[18px]'} leading-tight`} style={{ color: roleVar(role) }}>
        {value}
      </span>
    </div>
  )
}

/** Fire once when the element scrolls near the viewport — used to defer heavy widgets. */
export function useInView<T extends Element>(rootMargin = '200px'): [React.RefObject<T | null>, boolean] {
  const ref = useRef<T>(null)
  const [seen, setSeen] = useState(false)
  useEffect(() => {
    const el = ref.current
    if (!el || seen) return
    if (typeof IntersectionObserver === 'undefined') {
      const t = setTimeout(() => setSeen(true), 0)
      return () => clearTimeout(t)
    }
    const io = new IntersectionObserver(
      entries => {
        if (entries.some(e => e.isIntersecting)) {
          setSeen(true)
          io.disconnect()
        }
      },
      { rootMargin },
    )
    io.observe(el)
    return () => io.disconnect()
  }, [rootMargin, seen])
  return [ref, seen]
}

/** Is the element currently on screen? (For pausing animations.) */
export function useVisible<T extends Element>(): [React.RefObject<T | null>, boolean] {
  const ref = useRef<T>(null)
  const [visible, setVisible] = useState(false)
  useEffect(() => {
    const el = ref.current
    if (!el || typeof IntersectionObserver === 'undefined') return
    const io = new IntersectionObserver(entries => setVisible(entries.some(e => e.isIntersecting)), { rootMargin: '80px' })
    io.observe(el)
    return () => io.disconnect()
  }, [])
  return [ref, visible]
}

/** requestAnimationFrame loop that only runs while `active`. dt is in seconds (clamped). */
export function useRaf(active: boolean, tick: (dt: number, t: number) => void) {
  const tickRef = useRef(tick)
  useEffect(() => {
    tickRef.current = tick
  })
  useEffect(() => {
    if (!active) return
    let raf = 0
    let last = performance.now()
    const start = last
    const loop = (now: number) => {
      const dt = Math.min(0.05, (now - last) / 1000)
      last = now
      tickRef.current(dt, (now - start) / 1000)
      raf = requestAnimationFrame(loop)
    }
    raf = requestAnimationFrame(loop)
    return () => cancelAnimationFrame(raf)
  }, [active])
}

function subscribeReducedMotion(cb: () => void) {
  const mq = window.matchMedia('(prefers-reduced-motion: reduce)')
  mq.addEventListener('change', cb)
  return () => mq.removeEventListener('change', cb)
}

export function usePrefersReducedMotion(): boolean {
  return useSyncExternalStore(
    subscribeReducedMotion,
    () => window.matchMedia('(prefers-reduced-motion: reduce)').matches,
    () => false,
  )
}

/** Small toolbar row used under most widgets. */
export function Controls({ children, className = '' }: { children: ReactNode; className?: string }) {
  return <div className={`mt-4 flex flex-wrap items-center gap-x-6 gap-y-3 ${className}`}>{children}</div>
}
